import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { entityId } from '../../utils/entityId';
import { useIsMobileWeb } from '../../hooks/useIsMobileWeb';
import MobileListSearch from '../common/MobileListSearch';
import CoverArt from '../CoverArt';

// Single-column artist list modal.
//
// Each row shows avatar + name + a per-artist subtitle (e.g. "played 4
// tracks of …"). When `fetchTracksFor` is provided, clicking a row toggles
// an expansion that fetches and renders the underlying track list lazily —
// counts are upgraded to authoritative once the response lands.
const SORT_LABELS = {
    popularity: 'Popularity',
    recent: 'Recent activity',
    alphabetical: 'A–Z',
};

const ArtistsListModal = ({
    title,
    subtitle,
    artists,
    getSubtitle,
    onArtistClick,
    onClose,
    fetchTracksFor,
    getInlineMixes,
    onTrackClick,
    onMixClick,
    sortModes = ['popularity', 'recent'],
}) => {
    const [expandedId, setExpandedId] = useState(null);
    const [tracksByArtist, setTracksByArtist] = useState({}); // id → { tracks, total, loading, error }
    const [filter, setFilter] = useState('');
    const isMobileWeb = useIsMobileWeb();
    const [sortBy, setSortBy] = useState(sortModes[0] || 'popularity');

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    // 'popularity' preserves the parent-supplied order (already ranked by
    // the backend). 'recent' sorts by lastPlayedDate descending — artists
    // missing a date sink to the bottom so they don't pollute the top.
    // 'alphabetical' sorts by name (A–Z, locale-aware, case-insensitive).
    const sortedArtists =
        sortBy === 'recent'
            ? [...artists].sort((a, b) => {
                const ad = a.lastPlayedDate || '';
                const bd = b.lastPlayedDate || '';
                if (ad === bd) return 0;
                if (!ad) return 1;
                if (!bd) return -1;
                return bd.localeCompare(ad);
            })
            : sortBy === 'alphabetical'
                ? [...artists].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
                : artists;

    const filteredArtists = filter.trim()
        ? sortedArtists.filter(a => (a.name || '').toLowerCase().includes(filter.trim().toLowerCase()))
        : sortedArtists;

    const handleToggleExpand = useCallback(async (artist) => {
        const key = entityId(artist) || artist.name;
        if (expandedId === key) { setExpandedId(null); return; }
        setExpandedId(key);
        if (!fetchTracksFor || tracksByArtist[key]?.tracks) return;
        setTracksByArtist(prev => ({ ...prev, [key]: { ...(prev[key] || {}), loading: true } }));
        try {
            const result = await fetchTracksFor(artist);
            setTracksByArtist(prev => ({
                ...prev,
                [key]: { tracks: result.tracks || [], total: result.total ?? null, loading: false },
            }));
        } catch (err) {
            setTracksByArtist(prev => ({
                ...prev,
                [key]: { tracks: [], loading: false, error: err?.message || 'Failed to load' },
            }));
        }
    }, [expandedId, fetchTracksFor, tracksByArtist]);

    return ReactDOM.createPortal(
        <div className="djpal-modal-backdrop" onMouseDown={onClose}>
            <div className="djpal-modal djpal-artists-list-modal" onMouseDown={e => e.stopPropagation()}>
                <div className="djpal-modal-header">
                    <div className="djpal-modal-title-group">
                        <h3 className="djpal-modal-title">{title}</h3>
                        {subtitle && <div className="djpal-modal-subtitle">{subtitle}</div>}
                    </div>
                    <button className="djpal-modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="djpal-modal-body">
                    {artists.length > 0 && (
                        <>
                            {isMobileWeb ? (
                                <div className="djpal-artists-list-modal-search djpal-artists-list-modal-search--mobile">
                                    <MobileListSearch value={filter} onChange={setFilter} placeholder="Filter artists" ariaLabel="Filter artists" />
                                </div>
                            ) : (
                                <div className="djpal-artists-list-modal-search">
                                    <input
                                        type="text"
                                        placeholder="Filter artists…"
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                        aria-label="Filter artists"
                                    />
                                    {filter && (
                                        <button type="button" className="djpal-artists-list-modal-search-clear" onClick={() => setFilter('')} aria-label="Clear filter">×</button>
                                    )}
                                </div>
                            )}
                            {sortModes.length > 1 && (
                                <div className="djpal-artists-list-modal-sort">
                                    <span className="djpal-artists-list-modal-sort-label">Sort:</span>
                                    {sortModes.map((mode, i) => (
                                        <React.Fragment key={mode}>
                                            {i > 0 && <span className="djpal-artists-list-modal-sort-sep">·</span>}
                                            <button
                                                type="button"
                                                className={`djpal-artists-list-modal-sort-link${sortBy === mode ? ' is-active' : ''}`}
                                                onClick={() => setSortBy(mode)}
                                            >
                                                {SORT_LABELS[mode] || mode}
                                            </button>
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                    {artists.length === 0 ? (
                        <div className="djpal-empty-hint">No artists found.</div>
                    ) : filteredArtists.length === 0 ? (
                        <div className="djpal-empty-hint">No matches for "{filter.trim()}"</div>
                    ) : (
                        <div className="djpal-artists-list-modal-list">
                            {filteredArtists.map((a, i) => {
                                const key = entityId(a) || a.name;
                                const sub = getSubtitle ? getSubtitle(a) : null;
                                const expanded = expandedId === key;
                                const detail = tracksByArtist[key];
                                const expandable = !!fetchTracksFor;
                                // Always-visible mix list per artist (used by the
                                // track-view supporters modal — data is local so
                                // there's no fetch step, just render).
                                const inlineMixes = getInlineMixes ? (getInlineMixes(a) || []) : null;
                                return (
                                    <div key={`${key}-${i}`} className={`djpal-artists-list-modal-row${expanded ? ' is-expanded' : ''}`}>
                                        <div
                                            className="djpal-artists-list-modal-head clickable"
                                            onClick={() => expandable ? handleToggleExpand(a) : onArtistClick?.(a)}
                                        >
                                            {a.artworkUrl ? (
                                                <CoverArt className="artist-avatar" src={a.artworkUrl} size={24} alt={a.name} />
                                            ) : (
                                                <div className="artist-avatar">{(a.name || '?').charAt(0).toUpperCase()}</div>
                                            )}
                                            <div className="djpal-artists-list-modal-meta">
                                                <span className="djpal-artists-list-modal-name">{a.name}</span>
                                                {sub && <span className="djpal-artists-list-modal-sub">{sub}</span>}
                                            </div>
                                            {expandable && (
                                                <span className="djpal-artists-list-modal-chevron" aria-hidden>{expanded ? '▾' : '▸'}</span>
                                            )}
                                        </div>
                                        {inlineMixes && inlineMixes.length > 0 && (
                                            <div className="djpal-artists-list-modal-mixes djpal-artists-list-modal-mixes--flush">
                                                {inlineMixes.map((mx, mi) => {
                                                    const titleParts = [mx.artist, mx.eventName].filter(Boolean);
                                                    const mixLabel = titleParts.join(mx.separator ? ` ${mx.separator} ` : ' – ') || mx.eventName || '(untitled mix)';
                                                    const date = mx.date || '';
                                                    return (
                                                        <div
                                                            key={`${key}-im-${mi}`}
                                                            className="djpal-artists-list-modal-mix clickable"
                                                            onClick={(e) => { e.stopPropagation(); onClose(); onMixClick?.(mx); }}
                                                            title={mixLabel}
                                                        >
                                                            {date && <span className="djpal-artists-list-modal-mix-date">{date}</span>}
                                                            <span className="djpal-artists-list-modal-mix-label">{mixLabel}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {expanded && expandable && (
                                            <div className="djpal-artists-list-modal-tracks">
                                                {detail?.loading && (
                                                    <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>
                                                )}
                                                {detail?.error && (
                                                    <div className="djpal-empty-hint">Couldn't load tracks: {detail.error}</div>
                                                )}
                                                {!detail?.loading && !detail?.error && detail?.tracks?.length === 0 && (
                                                    <div className="djpal-empty-hint">No tracks found.</div>
                                                )}
                                                {detail?.tracks?.map((t, ti) => {
                                                    const art = t.artworkUrl || t.artwork || '';
                                                    const label = t.artist ? `${t.artist} – ${t.title}` : t.title;
                                                    const trackKey = `${key}::${entityId(t) || ti}`;
                                                    const mixes = Array.isArray(t.mixes) ? t.mixes : [];
                                                    return (
                                                        <div key={`${key}-t-${ti}`} className="djpal-artists-list-modal-track-row">
                                                            <div
                                                                className="djpal-artists-list-modal-track clickable"
                                                                onClick={() => { onTrackClick?.(t); onClose(); }}
                                                                title={label}
                                                            >
                                                                {art ? (
                                                                    <CoverArt className="track-artwork-mini" src={art} size={28} />
                                                                ) : (
                                                                    <div className="track-artwork-mini placeholder">&#9835;</div>
                                                                )}
                                                                <span className="track-label">{label}</span>
                                                            </div>
                                                            {mixes.length > 0 && (
                                                                <div className="djpal-artists-list-modal-mixes">
                                                                    {mixes.map((mx, mi) => {
                                                                        const titleParts = [mx.artist, mx.eventName].filter(Boolean);
                                                                        const mixLabel = titleParts.join(mx.separator ? ` ${mx.separator} ` : ' – ') || mx.eventName || '(untitled mix)';
                                                                        const date = mx.date || '';
                                                                        return (
                                                                            <div
                                                                                key={`${trackKey}-m-${mi}`}
                                                                                className="djpal-artists-list-modal-mix clickable"
                                                                                onClick={() => { onMixClick?.(mx); onClose(); }}
                                                                                title={mixLabel}
                                                                            >
                                                                                {date && <span className="djpal-artists-list-modal-mix-date">{date}</span>}
                                                                                <span className="djpal-artists-list-modal-mix-label">{mixLabel}</span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="djpal-modal-footer">
                    {filter.trim()
                        ? `${filteredArtists.length} of ${artists.length} ${artists.length === 1 ? 'artist' : 'artists'}`
                        : `${artists.length} ${artists.length === 1 ? 'artist' : 'artists'}`}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default ArtistsListModal;
